/**
 * /settings — screen 3s.
 *
 * Ordered by frequency of use, not by the brief's order (D86): You,
 * Notifications, Your stuff, Your data, Appearance, About.
 *
 *   D87 — six independent per-type toggles, a quiet-hours window, a game-days
 *         master switch, and — under an "Also" divider because it is where
 *         people look for "stop putting my name in things" — the display-only
 *         `feature_me_in_stats` preference.
 *   D88 — the name row carries its claim status inline, because "anyone could
 *         take this name" is only actionable if you can see it.
 *   D91 — push is an enhancement. The callout offers it; it never blocks, and
 *         the explainer says plainly that everything still shows up in the app.
 *   D21 — export and delete are here because the Privacy Policy promises them.
 *
 * Every write is a `PATCH /api/me` applied optimistically against a local
 * overlay, so a toggle moves under the thumb and rolls back only if the server
 * actually refuses.
 */

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { NotifPrefs, User } from '@shared/types'
import { TEAMS, teamLogoUrl, teamName } from '@shared/teams'
import { useStore } from '../lib/store'
import { ApiFailure, DEL, GET, PATCH, POST } from '../lib/api'
import {
  Body, Button, Card, Divider, Eyebrow, Field, Header, Notice, Screen, Sheet, Spacer, Toggle,
} from '../ui'

const APP_VERSION = 'v1.2.0'
const TERMS_VERSION = 2
const MASKED_EMAIL_KEY = 'sfg.maskedEmail'

const DATE_SHORT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })

const ROW: CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
  width: '100%', textAlign: 'left', color: 'var(--fg)',
  padding: '14px 0', borderBottom: '1px solid var(--line)',
  fontSize: 14, minHeight: 48,
}

// ── the six notification types, in the order 06-notifications.md §2 lists them ─

const NOTIF_ROWS: { key: keyof NotifPrefs; label: string }[] = [
  { key: 'challenge_resolved', label: 'Challenge resolved' },
  { key: 'challenge_proposed', label: 'Challenge proposed' },
  { key: 'team_final', label: 'Your team’s final score' },
  { key: 'food_arriving', label: 'Food arriving' },
  { key: 'attendance_poll', label: 'Notify before Sunday' },
  { key: 'fund_reminder', label: 'Food Fund reminder' },
]

// ── device / push helpers (D91) ─────────────────────────────────────────────

interface SafariNavigator extends Navigator { standalone?: boolean }

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/** iOS Safari cannot even ask for push until the site is on the Home Screen. */
function iosNeedsInstall(): boolean {
  if (!isIos()) return false
  const n = navigator as SafariNavigator
  if (n.standalone === false) return true
  if (typeof n.standalone === 'boolean') return false
  return !(window.matchMedia?.('(display-mode: standalone)').matches ?? false)
}

function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
}

/** VAPID keys arrive base64url; `applicationServerKey` wants raw bytes. */
function vapidKeyToBuffer(base64: string): ArrayBuffer {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const buffer = new ArrayBuffer(raw.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i)
  return buffer
}


// ── profile side-load, for the counts in "Your stuff" ───────────────────────

interface ProfileCounts {
  attendance: { weeks: number; total: number; streak: number }
  picks: { points: number; rank: number } | null
}

export default function Settings() {
  const { state, refresh, toast } = useStore()
  const navigate = useNavigate()

  const [over, setOver] = useState<Partial<User>>({})
  const [err, setErr] = useState<string | null>(null)
  const [counts, setCounts] = useState<ProfileCounts | null>(null)

  const [teamSheet, setTeamSheet] = useState<null | 'primary' | 'secondary'>(null)
  const [nameSheet, setNameSheet] = useState(false)
  const [emailSheet, setEmailSheet] = useState(false)
  const [pushSheet, setPushSheet] = useState(false)
  const [deleteSheet, setDeleteSheet] = useState(false)
  const [communitySheet, setCommunitySheet] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const [pushPermission, setPushPermission] = useState<NotificationPermission | 'unsupported'>(
    () => (pushSupported() ? Notification.permission : 'unsupported'),
  )

  const user = state ? { ...state.user, ...over } : null

  useEffect(() => {
    if (!state) return
    const ac = new AbortController()
    void (async () => {
      try {
        setCounts(await GET<ProfileCounts>(`/api/users/${encodeURIComponent(state.user.id)}`, ac.signal))
      } catch {
        // A missing count is a missing count; the screen is still usable.
      }
    })()
    return () => ac.abort()
  }, [state?.user.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const patch = useCallback(async (fields: Partial<User>, body: Record<string, unknown>) => {
    setOver((o) => ({ ...o, ...fields }))
    try {
      await PATCH('/api/me', body)
      await refresh(true)
      setOver({})
      setErr(null)
    } catch (e) {
      // Roll the optimistic fields back; leave anything else the user changed.
      setOver((o) => {
        const next = { ...o }
        for (const k of Object.keys(fields)) delete next[k as keyof User]
        return next
      })
      const message = e instanceof ApiFailure ? e.message : 'That change didn’t save.'
      setErr(message)
      toast(message)
    }
  }, [refresh, toast])

  const signOut = useCallback(async () => {
    setSigningOut(true)
    try {
      await POST('/api/auth/signout', {})
    } catch {
      // The cookie is cleared by the response either way, and a failed revoke
      // is not a reason to keep somebody on a screen they asked to leave.
    }
    // A full load rather than a router navigation: the store, the cached team
    // colours and any in-flight fetch all belong to the session that just
    // ended, and the cheapest way to be sure none of them survive is to start
    // the app over.
    window.location.assign('/join')
  }, [])

  const setPref = useCallback((key: keyof NotifPrefs, value: boolean) => {
    if (!user) return
    const next: NotifPrefs = { ...user.notifPrefs, [key]: value }
    void patch({ notifPrefs: next }, { notifPrefs: { [key]: value } })
  }, [patch, user])

  // ── push enablement (D91) ─────────────────────────────────────────────────

  const pushActive = pushPermission === 'granted'

  const enablePush = useCallback(async () => {
    if (!pushSupported()) {
      toast('This browser can’t do push. Everything still shows up in the app.')
      return
    }
    try {
      const permission = await Notification.requestPermission()
      setPushPermission(permission)
      if (permission !== 'granted') {
        toast('No problem. Everything still shows up in the app.')
        return
      }
      const { publicKey } = await GET<{ publicKey: string | null }>('/api/push/key')
      if (!publicKey) {
        toast('Push isn’t configured on this deployment yet.')
        return
      }
      const reg = await navigator.serviceWorker.ready
      const existing = await reg.pushManager.getSubscription()
      const sub = existing ?? await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKeyToBuffer(publicKey),
      })
      await POST('/api/push/subscribe', sub.toJSON())
      setPushSheet(false)
      toast('Push is on for this device.')
    } catch (e) {
      toast(e instanceof ApiFailure ? e.message : 'Couldn’t turn push on here. The app still shows everything.')
    }
  }, [toast])

  // ── data (D21) ────────────────────────────────────────────────────────────

  const downloadData = useCallback(() => {
    // Same-origin, cookie-authenticated, served with a content-disposition.
    window.location.assign('/api/me/export')
  }, [])

  if (!user || !state) {
    return (
      <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Header bordered={false} title={<span style={{ fontSize: 20, fontWeight: 800 }}>⚙️ Settings</span>} />
        <Body><Spacer h={8} /></Body>
      </Screen>
    )
  }

  const challengeCounts = state.challenges.counts
  const myContributionsC = state.fund
    ? state.fund.contributions
      .filter((c) => c.userId === user.id && (c.status === 'confirmed' || c.status === 'claimed'))
      .reduce((s, c) => s + c.amountC, 0)
    : null

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        title={<span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>⚙️ Settings</span>}
        right={
          // Settings is reached from the avatar on Home and from a dozen
          // "→ Settings" links, and the bottom bar does not carry it, so
          // leaving used to mean the browser's back gesture or Home. An X is
          // the control people look for on a screen that behaves like a panel.
          <button
            onClick={() => navigate('/')}
            aria-label="Close settings"
            style={{
              width: 40, height: 40, borderRadius: 12, flex: '0 0 auto',
              border: '1px solid var(--outline)', color: 'var(--fg)',
              fontSize: 16, fontWeight: 700, lineHeight: 1,
            }}
          >
            ✕
          </button>
        }
      />

      <Body style={{ padding: '6px var(--gutter) calc(24px + var(--nav-h))' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={12} /></> : null}

        {/* ── YOU ─────────────────────────────────────────────────────────── */}
        <Eyebrow style={{ marginTop: 8 }}>You</Eyebrow>

        <button style={ROW} onClick={() => setNameSheet(true)}>
          <span>Name</span>
          <span style={{ color: 'var(--dim)' }}>
            {user.displayName}{user.nameState === 'claimed' ? ' ✓' : ''} →
          </span>
        </button>
        <div style={{ fontSize: 12, color: 'var(--dim)', padding: '8px 0 2px', lineHeight: 1.5 }}>
          {/* `user.isVerified` and `user.emailMasked` both come off the row, so
              signing in from the emailed link on a phone shows as confirmed
              there. This used to read a masked address out of sessionStorage,
              which is only ever populated on the device that asked for the
              email — so the one flow that proves an address was the one flow
              that couldn't display it. */}
          {user.isVerified ? (
            <span>✓ Confirmed{user.emailMasked ? ` · ${user.emailMasked}` : ''}</span>
          ) : user.emailMasked ? (
            <>
              {user.emailMasked} isn’t confirmed yet.{' '}
              <button
                onClick={() => setEmailSheet(true)}
                style={{ color: 'var(--bright)', fontWeight: 600, fontSize: 12, minHeight: 32 }}
              >
                Send the link →
              </button>
            </>
          ) : (
            <>
              Anyone could take this name.{' '}
              <button
                onClick={() => setEmailSheet(true)}
                style={{ color: 'var(--bright)', fontWeight: 600, fontSize: 12, minHeight: 32 }}
              >
                Lock it in →
              </button>
            </>
          )}
        </div>

        <button style={ROW} onClick={() => setTeamSheet('primary')}>
          <span>Team</span>
          <span style={{ color: 'var(--bright)' }}>{teamName(user.teamId)} →</span>
        </button>

        <button style={ROW} onClick={() => setTeamSheet('secondary')}>
          <span>Also rooting for</span>
          <span style={{ color: 'var(--dim)' }}>
            {user.secondaryTeamId ? teamName(user.secondaryTeamId) : 'none'} →
          </span>
        </button>

        {/* ── NOTIFICATIONS ───────────────────────────────────────────────── */}
        <Eyebrow style={{ marginTop: 22 }}>Notifications</Eyebrow>

        {NOTIF_ROWS.map((n) => (
          <div key={n.key} style={{ ...ROW, padding: '13px 0', minHeight: 46 }}>
            <span id={`notif-${n.key}`}>{n.label}</span>
            <Toggle
              on={user.notifPrefs[n.key]}
              onChange={(v) => setPref(n.key, v)}
              label={n.label}
            />
          </div>
        ))}

        <div style={{ ...ROW, padding: '13px 0' }}>
          <span>Game days only</span>
          <Toggle
            on={user.notifPrefs.game_days_only}
            onChange={(v) => setPref('game_days_only', v)}
            label="Game days only"
          />
        </div>

        <div
          className="mono"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase',
            color: 'var(--dim)', margin: '18px 0 4px',
          }}
        >
          <Divider style={{ flex: 1 }} />
          <span>Also</span>
          <Divider style={{ flex: 1 }} />
        </div>

        <div style={{ ...ROW, padding: '13px 0' }}>
          <span>
            Feature me in room stats
            <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 2 }}>
              Keeps your name out of “Best call so far” and the pick’em shout-outs.
            </div>
          </span>
          <Toggle
            on={user.notifPrefs.feature_me_in_stats}
            onChange={(v) => setPref('feature_me_in_stats', v)}
            label="Feature me in room stats"
          />
        </div>

        {!pushActive ? (
          <Card style={{ marginTop: 12, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13 }}>
            <span>⚠️ Push isn’t on for this device</span>
            <button
              onClick={() => setPushSheet(true)}
              style={{ color: 'var(--bright)', fontWeight: 600, fontSize: 13, flex: '0 0 auto', minHeight: 44 }}
            >
              Turn on →
            </button>
          </Card>
        ) : null}

        {/* ── YOUR STUFF (D85) ────────────────────────────────────────────── */}
        <Eyebrow style={{ marginTop: 22 }}>Your stuff</Eyebrow>

        <button style={ROW} onClick={() => navigate('/challenges')}>
          <span>Challenges</span>
          <span style={{ color: 'var(--dim)' }}>
            {challengeCounts.open} open, {challengeCounts.resolved} resolved →
          </span>
        </button>

        <button style={ROW} onClick={() => navigate('/fund')}>
          <span>Contributions</span>
          <span style={{ color: 'var(--dim)' }}>
            {myContributionsC === null ? '-' : `$${Math.round(myContributionsC / 100)} this week`} →
          </span>
        </button>

        <button style={ROW} onClick={() => navigate('/attendance')}>
          <span>Attendance</span>
          <span style={{ color: 'var(--dim)' }}>
            {counts ? `${counts.attendance.weeks} of ${counts.attendance.total} weeks` : '-'} →
          </span>
        </button>

        {/* ── YOUR DATA (D21) ─────────────────────────────────────────────── */}
        <Eyebrow style={{ marginTop: 22 }}>Your data</Eyebrow>

        <button style={ROW} onClick={downloadData}>
          <span>Download my data</span>
          <span style={{ color: 'var(--dim)' }}>→</span>
        </button>

        <button style={ROW} onClick={() => setDeleteSheet(true)}>
          <span>Delete my account</span>
          <span style={{ color: 'var(--dim)' }}>→</span>
        </button>

        {/* ── APPEARANCE (D89) ────────────────────────────────────────────── */}
        <Eyebrow style={{ marginTop: 22 }}>Appearance</Eyebrow>

        <div style={ROW}>
          <label htmlFor="theme-select">Theme</label>
          <select
            id="theme-select"
            value={user.theme}
            onChange={(e) => {
              const theme = e.target.value as User['theme']
              void patch({ theme }, { theme })
            }}
            style={selectStyle}
          >
            <option value="system">System</option>
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </div>

        <div style={ROW}>
          <label htmlFor="textsize-select">Text size</label>
          <select
            id="textsize-select"
            value={user.textSize}
            onChange={(e) => {
              const textSize = e.target.value as User['textSize']
              void patch({ textSize }, { textSize })
            }}
            style={selectStyle}
          >
            <option value="normal">Normal</option>
            <option value="large">Large</option>
          </select>
        </div>

        {/* ── ABOUT ───────────────────────────────────────────────────────── */}
        <Eyebrow style={{ marginTop: 22 }}>About</Eyebrow>

        <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ ...ROW, color: 'var(--fg)' }}>
          <span>Terms of Use</span>
          <span style={{ color: 'var(--dim)' }} aria-label="opens in a new tab">↗</span>
        </a>

        <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ ...ROW, color: 'var(--fg)' }}>
          <span>Privacy Policy</span>
          <span style={{ color: 'var(--dim)' }} aria-label="opens in a new tab">↗</span>
        </a>

        <Link to="/terms" style={{ ...ROW, color: 'var(--fg)' }}>
          <span>Agreement</span>
          <span style={{ color: 'var(--dim)' }}>
            v{TERMS_VERSION}, accepted {DATE_SHORT.format(user.createdAt)} →
          </span>
        </Link>

        <a href="/terms/history" target="_blank" rel="noopener noreferrer" style={{ ...ROW, color: 'var(--fg)' }}>
          <span>What’s changed</span>
          <span style={{ color: 'var(--dim)' }} aria-label="opens in a new tab">↗</span>
        </a>

        {user.isAdmin ? (
          <Link to="/admin" style={{ ...ROW, color: 'var(--fg)' }}>
            <span>Admin console</span>
            <span style={{ color: 'var(--dim)' }}>→</span>
          </Link>
        ) : null}

        {/* ── COMMUNITY ───────────────────────────────────────────────────
            It is a door, not a setting: Shouts, Online, Picks and Feats switch
            off and you stop appearing in the lists those features draw.
            Nothing is deleted, and opting back in is one tap with no
            confirmation, because a decision that is easy to reverse does not
            need to be defended. */}
        <Eyebrow style={{ marginTop: 26 }}>Community</Eyebrow>

        {user.optedOut ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 10, lineHeight: 1.5 }}>
              You’re opted out. Shouts, Online, Picks and Feats are off, and you don’t appear in
              those lists. Everything you wrote is still here.
            </div>
            <Spacer h={12} />
            <Button
              variant="secondary"
              onClick={() => { void patch({ optedOut: false }, { optedOut: false }) }}
            >
              Opt back into community
            </Button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 10, lineHeight: 1.5 }}>
              Turns off Shouts, Online, Picks and Feats, and hides you from those lists. Check in,
              Chip in, Food and the schedule keep working.
            </div>
            {/* A link at the size of the paragraph above it, not a button.
                The weight of a control is a claim about how often it is
                wanted, and a full-width secondary button sitting under two
                lines of explanation read as an invitation to press it. */}
            <button
              onClick={() => setCommunitySheet(true)}
              style={{
                display: 'inline-block', marginTop: 10, padding: '11px 0', minHeight: 44,
                fontSize: 13, color: 'var(--bright)', textAlign: 'left',
                textDecoration: 'underline', textUnderlineOffset: 3,
              }}
            >
              Opt out of community
            </button>
          </>
        )}

        {/* ── SESSION ─────────────────────────────────────────────────────
            The bottom of the screen. It ends only THIS session: other devices
            stay signed in, which is what the sessions list further up is for. */}
        <Eyebrow style={{ marginTop: 26 }}>Session</Eyebrow>
        <Spacer h={10} />
        <Button variant="secondary" disabled={signingOut} onClick={() => { void signOut() }}>
          {signingOut ? 'Logging out…' : 'Log out'}
        </Button>
        <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>
          Signs you out on this device only. With a confirmed email you can get back in from a
          link; without one, you’ll need the promo code again.
        </div>

        <div className="mono" style={{ fontSize: 11, color: 'var(--dim)', marginTop: 26 }}>
          {APP_VERSION}
        </div>
      </Body>

      {/* ── sheets ─────────────────────────────────────────────────────────── */}

      <TeamSheet
        mode={teamSheet}
        currentPrimary={user.teamId}
        currentSecondary={user.secondaryTeamId}
        onClose={() => setTeamSheet(null)}
        onPick={(teamId) => {
          if (teamSheet === 'primary') {
            if (teamId) void patch({ teamId }, { teamId })
          } else {
            void patch({ secondaryTeamId: teamId }, { secondaryTeamId: teamId })
          }
          setTeamSheet(null)
        }}
      />

      <NameSheet
        open={nameSheet}
        current={user.displayName}
        onClose={() => setNameSheet(false)}
        onSave={async (displayName) => {
          await patch({ displayName }, { displayName })
          setNameSheet(false)
        }}
      />

      <EmailSheet
        open={emailSheet}
        onClose={() => setEmailSheet(false)}
        onSent={(masked, email) => {
          try {
            // The masked form is safe to persist; the address itself is passed
            // in router state only, so it lives in memory and nowhere else.
            sessionStorage.setItem(MASKED_EMAIL_KEY, masked)
            sessionStorage.setItem('sfg.verifyMode', 'verify')
          } catch { /* private mode */ }
          setEmailSheet(false)
          navigate('/verify', { state: { mode: 'verify', masked, email } })
        }}
      />

      <PushSheet
        open={pushSheet}
        onClose={() => setPushSheet(false)}
        onEnable={() => { void enablePush() }}
        permission={pushPermission}
      />

      <DeleteSheet
        open={deleteSheet}
        displayName={user.displayName}
        onClose={() => setDeleteSheet(false)}
      />

      <OptOutSheet
        open={communitySheet}
        onClose={() => setCommunitySheet(false)}
        onConfirm={async () => {
          await patch({ optedOut: true }, { optedOut: true })
          setCommunitySheet(false)
        }}
      />
    </Screen>
  )
}

const selectStyle: CSSProperties = {
  background: 'var(--surf)', border: '1px solid var(--line)', borderRadius: 12,
  color: 'var(--fg)', fontSize: 14, padding: '10px 12px', minHeight: 44,
}

// ── team picker ─────────────────────────────────────────────────────────────

function TeamSheet({
  mode, currentPrimary, currentSecondary, onClose, onPick,
}: {
  mode: null | 'primary' | 'secondary'
  currentPrimary: string
  currentSecondary: string | null
  onClose: () => void
  onPick: (teamId: string | null) => void
}) {
  const [q, setQ] = useState('')
  useEffect(() => { if (mode) setQ('') }, [mode])

  const selected = mode === 'primary' ? currentPrimary : currentSecondary
  const needle = q.trim().toLowerCase()
  const teams = needle
    ? TEAMS.filter((t) =>
      t.location.toLowerCase().includes(needle)
      || t.nickname.toLowerCase().includes(needle)
      || t.abbr.toLowerCase().includes(needle))
    : TEAMS

  return (
    <Sheet open={mode !== null} onClose={onClose} labelledBy="team-sheet-title">
      <div id="team-sheet-title" style={{ fontSize: 18, fontWeight: 800 }}>
        {mode === 'secondary' ? 'Also rooting for' : 'Your team'}
      </div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.45 }}>
        {mode === 'secondary'
          ? 'A second team, shown on your profile. It doesn’t change the app’s colours.'
          : 'This re-themes the whole app and decides which games get highlighted for you.'}
      </div>

      <div style={{ marginTop: 12 }}>
        <label htmlFor="team-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          Search teams
        </label>
        <input
          id="team-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search city, nickname, or abbreviation"
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 12, minHeight: 44,
            border: '1px solid var(--line)', background: 'var(--bg)', fontSize: 13, color: 'var(--fg)',
          }}
        />
      </div>

      {mode === 'secondary' ? (
        <button
          onClick={() => onPick(null)}
          style={{
            width: '100%', textAlign: 'left', padding: '12px 0', minHeight: 44,
            borderBottom: '1px solid var(--line)', marginTop: 8, fontSize: 14,
            color: currentSecondary === null ? 'var(--bright)' : 'var(--fg)',
          }}
        >
          Nobody, just my team
        </button>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 12 }}>
        {teams.map((t) => (
          <TeamTile
            key={t.id}
            teamId={t.id}
            abbr={t.abbr}
            name={`${t.location} ${t.nickname}`}
            selected={selected === t.id}
            onSelect={() => onPick(t.id)}
          />
        ))}
      </div>
      {teams.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 14 }}>No team matches “{q}”.</div>
      ) : null}
      <Spacer h={8} />
    </Sheet>
  )
}

/** A square team tile: ESPN logo, abbreviation as the fallback and the label. */
function TeamTile({
  teamId, abbr, name, selected, onSelect,
}: {
  teamId: string; abbr: string; name: string; selected: boolean; onSelect: () => void
}) {
  const [broken, setBroken] = useState(false)
  const src = teamLogoUrl(teamId)
  return (
    <button
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={name}
      style={{
        aspectRatio: '1', borderRadius: 12, width: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: selected ? 'var(--surf)' : 'transparent',
        border: selected ? '2px solid var(--fill)' : '1px solid var(--line)',
        padding: 6,
      }}
    >
      {broken || !src ? (
        <span
          className="mono"
          style={{ fontSize: 12, fontWeight: 700, color: selected ? 'var(--fg)' : 'var(--dim)' }}
        >
          {abbr}
        </span>
      ) : (
        <img
          src={src}
          alt={abbr}
          onError={() => setBroken(true)}
          style={{ width: '78%', height: '78%', objectFit: 'contain' }}
        />
      )}
    </button>
  )
}

// ── name ────────────────────────────────────────────────────────────────────

function NameSheet({
  open, current, onClose, onSave,
}: { open: boolean; current: string; onClose: () => void; onSave: (name: string) => Promise<void> }) {
  const [value, setValue] = useState(current)
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (open) setValue(current) }, [open, current])

  return (
    <Sheet open={open} onClose={onClose} labelledBy="name-sheet-title">
      <div id="name-sheet-title" style={{ fontSize: 18, fontWeight: 800 }}>Your name</div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.45 }}>
        Everything you’ve ever done stays yours. History follows the account, not the name.
        You can change it once a day.
      </div>
      <Spacer h={14} />
      <Field
        label="Display name"
        value={value}
        inSheet
        maxLength={24}
        autoComplete="nickname"
        onChange={(e) => setValue(e.target.value)}
      />
      <Spacer h={16} />
      <Button
        disabled={busy || value.trim().length < 2 || value.trim() === current}
        onClick={() => {
          setBusy(true)
          void onSave(value.trim()).finally(() => setBusy(false))
        }}
      >
        {busy ? 'Saving…' : 'Save'}
      </Button>
      <Spacer h={8} />
      <Button variant="tertiary" onClick={onClose}>Cancel</Button>
    </Sheet>
  )
}

// ── verification (D2 copy, verbatim) ────────────────────────────────────────

function EmailSheet({
  open, onClose, onSent,
}: { open: boolean; onClose: () => void; onSent: (masked: string, email: string) => void }) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { if (open) { setEmail(''); setError(null) } }, [open])

  const send = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await POST<{ maskedEmail: string }>('/api/auth/verify/start', { email: email.trim() })
      onSent(res.maskedEmail, email.trim())
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t send that link.')
    } finally {
      setBusy(false)
    }
  }, [email, onSent])

  return (
    <Sheet open={open} onClose={onClose} labelledBy="email-sheet-title">
      <div id="email-sheet-title" style={{ fontSize: 18, fontWeight: 800 }}>Lock in your name</div>
      <div style={{ fontSize: 14, color: 'var(--fg)', marginTop: 8, lineHeight: 1.5 }}>
        Enter an email and we’ll send you a link. Once you click it, your name is yours and nobody
        else can take it, on any device, ever.
      </div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 10, lineHeight: 1.5 }}>
        We use your email for three things and nothing else: this link, signing you back in if you
        lose your session, and one question on Fridays about whether you’re coming. We never share
        it.{' '}
        <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--bright)' }}>
          Privacy Policy
        </a>
      </div>
      <Spacer h={14} />
      <Field
        label="Email address"
        type="email"
        inputMode="email"
        autoComplete="email"
        value={email}
        inSheet
        error={error}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Spacer h={16} />
      <Button disabled={busy || !email.includes('@')} onClick={() => { void send() }}>
        {busy ? 'Sending…' : 'Send the link'}
      </Button>
      <Spacer h={8} />
      <Button variant="tertiary" onClick={onClose}>Not now</Button>
    </Sheet>
  )
}

// The quiet-hours control is gone from this screen (the window itself still
// applies on the server, at its default, so nothing buzzes in the middle of the
// night). It was a two-select sheet for a preference nobody in a nine-person
// group was going to tune, on a screen whose job is the four things people do
// change.

// ── push explainer (D91) ────────────────────────────────────────────────────

function PushSheet({
  open, onClose, onEnable, permission,
}: {
  open: boolean; onClose: () => void; onEnable: () => void
  permission: NotificationPermission | 'unsupported'
}) {
  const needsInstall = iosNeedsInstall()

  return (
    <Sheet open={open} onClose={onClose} labelledBy="push-sheet-title">
      <div id="push-sheet-title" style={{ fontSize: 18, fontWeight: 800 }}>Turn on notifications</div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>
        Push is a bonus, not a requirement. Everything that sends a notification also shows up in
        the app, so you will never miss something because push is off.
      </div>

      {needsInstall ? (
        <>
          <Spacer h={16} />
          <div style={{ fontSize: 14, fontWeight: 700 }}>On iPhone, it takes three taps</div>
          <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
            Safari only allows notifications from sites saved to the Home Screen.
          </div>
          <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
            <InstallStep n={1} glyph="⬆️" title="Tap Share" detail="The square with an arrow out of it, at the bottom of Safari." />
            <InstallStep n={2} glyph="➕" title="Add to Home Screen" detail="Scroll the list. It’s below the row of sharing icons." />
            <InstallStep n={3} glyph="🏈" title="Open it from the icon" detail="Then come back here and tap Enable." />
          </div>
        </>
      ) : null}

      <Spacer h={18} />
      {permission === 'denied' ? (
        <Notice tone="warn">
          This browser has notifications blocked for the site. Turn them back on in your browser’s
          site settings, then come back.
        </Notice>
      ) : (
        <Button onClick={onEnable} disabled={permission === 'unsupported'}>
          {permission === 'unsupported' ? 'Not available on this browser' : 'Enable notifications'}
        </Button>
      )}
      <Spacer h={8} />
      <Button variant="tertiary" onClick={onClose}>Not now</Button>
    </Sheet>
  )
}

function InstallStep({
  n, glyph, title, detail,
}: { n: number; glyph: string; title: string; detail: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div
        aria-hidden
        style={{
          width: 40, height: 40, flex: '0 0 auto', borderRadius: 12,
          border: '1px solid var(--line)', background: 'var(--bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
        }}
      >
        {glyph}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{n}. {title}</div>
        <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 2, lineHeight: 1.45 }}>{detail}</div>
      </div>
    </div>
  )
}

// ── account deletion (D21, D99-style typed confirmation) ────────────────────

function DeleteSheet({
  open, displayName, onClose,
}: { open: boolean; displayName: string; onClose: () => void }) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { if (open) { setTyped(''); setError(null) } }, [open])

  const confirm = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await DEL('/api/me', { confirmName: typed.trim() })
      window.location.assign('/join')
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'That didn’t go through.')
      setBusy(false)
    }
  }, [typed])

  return (
    <Sheet open={open} onClose={onClose} labelledBy="delete-sheet-title">
      <div id="delete-sheet-title" style={{ fontSize: 18, fontWeight: 800 }}>Delete my account</div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>
        This removes your name, your email, your sessions and your pick’em picks. Records you share
        with other people (challenges you were part of, food orders on a shared day) are
        anonymized rather than deleted, because deleting your half of a challenge would erase
        someone else’s history too. Your name is freed for someone else after 30 days.
      </div>
      <Spacer h={14} />
      <Field
        label={`Type “${displayName}” to confirm`}
        value={typed}
        inSheet
        error={error}
        autoComplete="off"
        onChange={(e) => setTyped(e.target.value)}
      />
      <Spacer h={16} />
      <Button
        disabled={busy || typed.trim().toLowerCase() !== displayName.trim().toLowerCase()}
        onClick={() => { void confirm() }}
        style={
          busy || typed.trim().toLowerCase() !== displayName.trim().toLowerCase()
            ? undefined
            : { background: 'var(--alert-line)', color: 'var(--fg)' }
        }
      >
        {busy ? 'Deleting…' : 'Delete my account'}
      </Button>
      <Spacer h={8} />
      <Button variant="tertiary" onClick={onClose}>Keep my account</Button>
    </Sheet>
  )
}

// ── opting out of the community ─────────────────────────────────────────────

/**
 * One confirmation, and it says the reversible part out loud.
 *
 * "Are you sure?" on its own reads as a warning about something permanent. The
 * thing this actually does is temporary, and the sheet says so in the same
 * breath it asks — which is the difference between a decision somebody makes
 * and a decision somebody avoids.
 */
function OptOutSheet({
  open, onClose, onConfirm,
}: { open: boolean; onClose: () => void; onConfirm: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (open) setBusy(false) }, [open])

  return (
    <Sheet open={open} onClose={onClose} labelledBy="optout-title">
      <div id="optout-title" style={{ fontSize: 18, fontWeight: 800 }}>
        Are you sure? You can always opt back in
      </div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 10, lineHeight: 1.6 }}>
        Shouts, Online, Picks and Feats switch off, and you stop appearing in the lists those
        features draw — including everyone else’s @mention menu. Nothing is deleted: your shouts,
        your picks and your feats are all still there and come back the moment you opt in again.
        Check in, Chip in, Food and the schedule are unaffected.
      </div>
      <Spacer h={18} />
      <Button
        disabled={busy}
        onClick={() => { setBusy(true); void onConfirm() }}
      >
        {busy ? 'Opting out…' : 'Opt out of community'}
      </Button>
      <Spacer h={8} />
      <Button variant="tertiary" onClick={onClose}>Never mind</Button>
    </Sheet>
  )
}
